import { useState } from 'react'
import { withCountRu } from '@dorixona/utils'
import { OrderCard } from './OrderCard'
import type { BoardCard, BoardColumn } from './api'
import { canDropTo, columnEmpty, type Step } from './model'

export interface BoardProps {
  columns: BoardColumn[]
  canUpdate: boolean
  assigneeName: (id: number) => string
  onAdvance: (card: BoardCard, step: Step) => void
  onMoveDialog: (card: BoardCard) => void
  onDrop: (card: BoardCard, columnKey: string) => void
}

/**
 * Доска обработки. Колонки задаёт сервер: их состав, порядок и заголовки — это
 * поток статусов, а он живёт на бэкенде (R-45). «Требует решения» приходит
 * только сотруднику всей сети — экран об этом не думает, он рисует что пришло.
 */
export function Board({
  columns,
  canUpdate,
  assigneeName,
  onAdvance,
  onMoveDialog,
  onDrop,
}: BoardProps) {
  const [dragged, setDragged] = useState<BoardCard | null>(null)
  const [over, setOver] = useState<string | null>(null)

  return (
    <div className="ord-board">
      {columns.map((column) => {
        const drop = dragged ? (canDropTo(dragged, column.key, canUpdate) ? 'can' : 'cant') : null
        // Жёлтый счётчик — только у очереди, которая ждёт аптеку: новые заказы.
        const waiting = column.key === 'new' && column.total > 0

        return (
          <section
            key={column.key}
            className="ui-col"
            aria-label={column.title}
            data-drop={drop}
            data-over={over === column.key}
            onDragOver={(event) => {
              // Бросить можно в любую колонку, если роль вправе менять заказы:
              // почему перенос не состоялся, объясняет сообщение, а не тишина.
              if (!canUpdate) return
              event.preventDefault()
              setOver(column.key)
            }}
            onDragLeave={() => {
              setOver(null)
            }}
            onDrop={(event) => {
              event.preventDefault()
              setOver(null)
              if (dragged && canUpdate) onDrop(dragged, column.key)
              setDragged(null)
            }}
          >
            <div className="ui-col-head">
              {column.title}
              <span
                className="ui-count"
                data-attention={waiting || undefined}
                aria-label={withCountRu(column.total, ['заказ', 'заказа', 'заказов'])}
              >
                {column.total}
              </span>
            </div>
            {column.orders.length === 0 ? (
              <div className="ui-col-empty">{columnEmpty(column.key)}</div>
            ) : null}
            {column.orders.map((card) => (
              <OrderCard
                key={card.id}
                card={card}
                assigneeName={assigneeName(card.assigneeId)}
                canUpdate={canUpdate}
                dragging={dragged?.id === card.id}
                onAdvance={(step) => {
                  onAdvance(card, step)
                }}
                onMove={() => {
                  onMoveDialog(card)
                }}
                onDragStart={() => {
                  setDragged(card)
                }}
                onDragEnd={() => {
                  setDragged(null)
                  setOver(null)
                }}
              />
            ))}
          </section>
        )
      })}
    </div>
  )
}
